import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Store, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fmtMilli, toMilli } from "../../lib/mshopCalc";
import { NumField } from "./ui";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";

/** Settings card for Mansori employees: one-time opening balance + guarded edit. */
export default function OpeningBalanceCard() {
    const [opening, setOpening] = useState(null);
    const [editing, setEditing] = useState(false);
    const [gold, setGold] = useState("");
    const [cash, setCash] = useState("");
    const [bank, setBank] = useState("");
    const [confirm, setConfirm] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/mshop/status").then((r) => setOpening(r.data.opening)).catch(() => {});
    useEffect(() => { load(); }, []);

    const valid = toMilli(gold) !== null && toMilli(cash) !== null && toMilli(bank) !== null;
    const submit = async () => {
        setBusy(true);
        try {
            if (opening.set) await api.put("/mshop/opening", { gold, cash, bank, confirm: true });
            else await api.post("/mshop/opening", { gold, cash, bank });
            toast.success("تم حفظ الرصيد الابتدائي");
            setEditing(false); setConfirm(false); await load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    if (!opening) return null;

    return (
        <section className="mt-6 rounded-2xl border border-[#E7E5DF] bg-white p-5 shadow-sm sm:p-6" dir="rtl" data-testid="mshop-opening-card">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold"><Store className="h-5 w-5 text-[#8A6A1F]" /> الرصيد الابتدائي للمحل</h2>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {[["الذهب المشترى (الكسر الافتتاحي)", opening.gold, "جم", "gold"], ["الكاش", opening.cash, "ر.ع", "cash"], ["البنك", opening.bank ?? "0", "ر.ع", "bank"]].map(([l, v, u, k]) => (
                    <div key={k} className="rounded-xl bg-[#F3F2EE] px-4 py-3">
                        <div className="text-xs text-neutral-500">{l}</div>
                        <div className="mt-1 font-bold" data-testid={`mshop-opening-${k}`}>
                            {opening.set ? <><span dir="ltr" className="font-numbers text-xl">{fmtMilli(v)}</span> <span className="text-xs">{u}</span></> : <span className="text-amber-700">غير محدد</span>}
                        </div>
                    </div>
                ))}
            </div>
            {editing ? (
                <div className="mt-4 space-y-4">
                    {opening.set ? (
                        <div className="flex gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-900 ring-1 ring-amber-200"><AlertTriangle className="h-4 w-4 shrink-0" /> تعديل الرصيد الابتدائي سيعيد احتساب أرصدة سجل المحل بالكامل.</div>
                    ) : null}
                    <NumField label="وزن الكسر الافتتاحي" unit="جرام" value={gold} onChange={setGold} testId="mshop-opening-gold-input" />
                    <NumField label="مبلغ الكاش في المحل" unit="ر.ع" value={cash} onChange={setCash} testId="mshop-opening-cash-input" />
                    <NumField label="مبلغ البنك في المحل" unit="ر.ع" value={bank} onChange={setBank} testId="mshop-opening-bank-input" />
                    <div className="flex gap-2">
                        <button onClick={() => setEditing(false)} className="h-11 rounded-xl border border-[#D9D6CE] px-4 text-sm font-bold" data-testid="mshop-opening-cancel">إلغاء</button>
                        <button disabled={!valid || busy} onClick={() => (opening.set ? setConfirm(true) : submit())} className="h-11 flex-1 rounded-xl bg-[#1F1D1A] text-sm font-bold text-[#F3DFA2] disabled:bg-[#A3A19D]" data-testid="mshop-opening-save">حفظ</button>
                    </div>
                </div>
            ) : (
                <button
                    onClick={() => { setGold(opening.gold || ""); setCash(opening.cash || ""); setBank(opening.bank ?? "0"); setEditing(true); }}
                    className="mt-4 h-11 rounded-xl bg-[#1F1D1A] px-5 text-sm font-bold text-[#F3DFA2]"
                    data-testid={opening.set ? "mshop-opening-edit-btn" : "mshop-opening-set-btn"}
                >
                    {opening.set ? "تعديل الرصيد الابتدائي" : "إدخال الرصيد الابتدائي"}
                </button>
            )}
            <AlertDialog open={confirm} onOpenChange={setConfirm}>
                <AlertDialogContent dir="rtl">
                    <AlertDialogHeader className="text-right sm:text-right">
                        <AlertDialogTitle>تأكيد تعديل الرصيد الابتدائي</AlertDialogTitle>
                        <AlertDialogDescription>سيتم إعادة احتساب الذهب المشترى والكاش والبنك في سجل المحل لكل الفترات بناءً على القيم الجديدة. هل أنت متأكد؟</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="mshop-opening-confirm-cancel">إلغاء</AlertDialogCancel>
                        <AlertDialogAction onClick={submit} className="bg-rose-700 hover:bg-rose-800" data-testid="mshop-opening-confirm-ok">تأكيد التعديل</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </section>
    );
}
